/**
 * Google Drive OAuth Callback Page
 * Handles the redirect from Google OAuth and communicates result to parent window.
 * Follows the same pattern as JiraCallback.js / GitHubCallback.js.
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Paper,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  CloudDone as CloudDoneIcon,
} from '@mui/icons-material';

const GoogleDriveCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const success = searchParams.get('success') === 'true';
    const error = searchParams.get('error');
    const errorMessage = searchParams.get('message');
    const name = searchParams.get('name');
    const email = searchParams.get('email');

    if (success) {
      setStatus('success');
      setMessage(`Successfully connected as ${decodeURIComponent(name || email || 'Google User')}`);

      if (window.opener) {
        window.opener.postMessage({
          type: 'GOOGLE_DRIVE_OAUTH_CALLBACK',
          success: true,
          name: name ? decodeURIComponent(name) : null,
          email: email ? decodeURIComponent(email) : null,
        }, window.location.origin);

        setTimeout(() => {
          window.close();
        }, 2000);
      }
    } else if (error) {
      setStatus('error');
      setMessage(errorMessage ? decodeURIComponent(errorMessage) : `Error: ${error}`);

      if (window.opener) {
        window.opener.postMessage({
          type: 'GOOGLE_DRIVE_OAUTH_CALLBACK',
          success: false,
          error: errorMessage ? decodeURIComponent(errorMessage) : error,
        }, window.location.origin);
      }
    }
  }, [searchParams]);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        bgcolor: 'grey.100',
        p: 2,
      }}
    >
      <Paper sx={{ p: 4, maxWidth: 450, width: '100%', textAlign: 'center', borderRadius: 3 }}>
        <CloudDoneIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          Google Drive Connection
        </Typography>

        {status === 'processing' && (
          <>
            <CircularProgress sx={{ my: 2 }} />
            <Typography color="text.secondary">Processing authentication…</Typography>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircleIcon sx={{ fontSize: 56, color: 'success.main', my: 2 }} />
            <Alert severity="success" sx={{ mt: 1, borderRadius: 2 }}>{message}</Alert>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              This window will close automatically…
            </Typography>
          </>
        )}

        {status === 'error' && (
          <>
            <ErrorIcon sx={{ fontSize: 56, color: 'error.main', my: 2 }} />
            <Alert severity="error" sx={{ mt: 1, borderRadius: 2 }}>{message}</Alert>
            <Button
              variant="outlined"
              sx={{ mt: 2 }}
              onClick={() => {
                if (window.opener) {
                  window.close();
                } else {
                  navigate('/settings');
                }
              }}
            >
              Close
            </Button>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default GoogleDriveCallback;
